import { useForm } from 'react-hook-form';
import { useTicketTemplates } from '@/hooks/useTicketTemplates';
import { useCatalog } from '@/hooks/useCatalog';
import { useTicketTypes } from '@/hooks/useTicketTypes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';

interface TicketTemplateFormProps {
  template?: any;
  onSuccess?: () => void;
}

export function TicketTemplateForm({ template, onSuccess }: TicketTemplateFormProps) {
  const { createTemplate, updateTemplate } = useTicketTemplates();
  const { items: catalog } = useCatalog();
  const { types: ticketTypes } = useTicketTypes();
  const { register, handleSubmit, watch, setValue } = useForm<any>({
    defaultValues: template || {}
  });

  const onSubmit = async (data: any) => {
    const payload = {
      ...data,
      sla_response_hours: data.sla_response_hours ? parseInt(data.sla_response_hours) : null,
      sla_resolution_hours: data.sla_resolution_hours ? parseInt(data.sla_resolution_hours) : null,
    };

    if (template) {
      await updateTemplate.mutateAsync({ id: template.id, ...payload });
    } else {
      await createTemplate.mutateAsync(payload);
    }
    onSuccess?.();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div>
        <Label>Ticket Type (sets name automatically)</Label>
        <Select
          onValueChange={(value) => {
            setValue('ticket_type_id', value, { shouldValidate: true });
            const selectedType = ticketTypes?.find((type: any) => String(type.id) === value);
            if (selectedType) {
              setValue('name', selectedType.name);
            }
          }}
          defaultValue={template?.ticket_type_id}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a ticket type" />
          </SelectTrigger>
          <SelectContent>
            {ticketTypes?.map((type: any) => (
              <SelectItem key={type.id} value={String(type.id)}>
                {type.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input type="hidden" {...register('ticket_type_id', { required: true })} />
        <input type="hidden" {...register('name', { required: true })} />
      </div>

      <div>
        <Label>Description / Instructions</Label>
        <Textarea {...register('description')} placeholder="Description and instructions for this ticket type..." rows={4} />
      </div>

      <div>
        <Label>Case Type (Sales Item)</Label>
        <Select 
          onValueChange={(value) => setValue('sales_item_id', value)}
          defaultValue={template?.sales_item_id}
        >
          <SelectTrigger>
            <SelectValue placeholder="Select a case type" />
          </SelectTrigger>
          <SelectContent>
            {catalog?.filter(item => item.type === 'case').map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>SLA First Response (hours)</Label>
          <Input type="number" {...register('sla_response_hours')} placeholder="24" />
        </div>
        <div>
          <Label>SLA Resolution (hours)</Label>
          <Input type="number" {...register('sla_resolution_hours')} placeholder="72" />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Label>Default Template</Label>
        <Switch 
          onCheckedChange={(checked) => setValue('is_default', checked)}
          defaultChecked={template?.is_default}
        />
      </div>

      <Button type="submit" className="w-full" disabled={createTemplate.isPending || updateTemplate.isPending}>
        {(createTemplate.isPending || updateTemplate.isPending) ? 'Saving...' : template ? 'Update Template' : 'Create Template'}
      </Button>
    </form>
  );
}
